import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthUser, authAPI, getAuthTokenFromResponse, normalizeMePayload } from '../api/auth';

type AuthContextType = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  refreshUser: () => Promise<AuthUser | null>;
};

const AUTH_TOKEN_KEY = 'authToken';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setUser(null);
      return null;
    }

    const response = await authAPI.getMe();
    const nextUser = normalizeMePayload(response.data);
    setUser(nextUser);
    return nextUser;
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      if (localStorage.getItem(AUTH_TOKEN_KEY)) {
        throw new Error('already_logged_in');
      }

      const loginResponse = await authAPI.login(email, password);
      const token = getAuthTokenFromResponse(loginResponse.data);

      if (!token) {
        throw new Error('token_missing');
      }

      localStorage.setItem(AUTH_TOKEN_KEY, token);
      const nextUser = await refreshUser();

      if (!nextUser) {
        throw new Error('user_missing');
      }

      return nextUser;
    },
    [refreshUser]
  );

  useEffect(() => {
    const bootstrap = async () => {
      try {
        await refreshUser();
      } catch (error) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    bootstrap().catch(() => {
      setIsLoading(false);
    });
  }, [refreshUser]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      login,
      logout,
      refreshUser,
    }),
    [user, isLoading, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
